import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import {
  ExperienceBookableItem,
  ExperienceOptionId,
  ExperiencePricingCategoryEnum,
  ExperiencesAgeBandEnum,
  ExperiencesPriceBreakdown,
  ExperiencesShopOptionRequest,
  IExperiencesAgeBandCounts,
  IExperiencesPassengerCounts,
  ParsedExperienceBookableItem,
  RewardsAccount,
} from "redmond";
import {
  Icon,
  IconName,
  ActionButton,
  MobileOutlinedTextInput,
  getPriceString,
  getRewardText,
  NotificationBanner,
  BannerSeverity,
  ActionLink,
  PickerType,
  SanitizedHtml,
} from "halifax";
import dayjs from "dayjs";
import clsx from "clsx";
import { min } from "lodash-es";

import styles from "./styles.module.scss";
import { ShopComponentSkeleton } from "../../component";
import * as textConstants from "../../textConstants";
import {
  TOTAL_NUM_TRAVELERS,
  START_TIME,
  CONTINUE_TEXT,
  DATE,
} from "../../../common/textConstants";
import { getMinMaxHelperText, IShopOverview } from "../ShopOverview/component";
import {
  getPaxMixFromTravelersCount,
  getTotalTravelersCount,
  MobileCalendarPicker,
} from "../../../common";
import { ExperiencesMobilePopoverCard } from "../../../common/ExperiencesMobilePopoverCard";
import {
  IShopCheckoutBreakdown,
  ShopCheckoutBreakdown,
} from "../ShopCheckoutBreakdown/component";
import { IShopCancellationPolicyProps } from "../ShopCancellationPolicy/component";
import { PATH_BOOK } from "../../../../utils/paths";
import { ShopCheckoutBreakdownSummary } from "../ShopCheckoutBreakdownSummary/component";
import { ParsedAgeBand } from "../../reducer";
import {
  AgeBandCountPickerTitles,
  PassengerCountPicker,
  PassengerCountPickerTitles,
  TravelerCountPickerTitles,
} from "../ShopPassengerCountPicker/component";

export interface IShopMobileAvailabilityCardProps extends IShopOverview {
  setFromDate: (date: Date | null) => void;
  setUntilDate: (date: Date | null) => void;
  fromDate: Date | null;
  untilDate: Date | null;
  fetchExperiencesShop: () => void;
  fetchExperiencesShopOptions: (request: ExperiencesShopOptionRequest) => void;
  selectedBookableItem: ExperienceBookableItem | null;
  setSelectedBookableItem: (
    selectedBookableItem: ExperienceBookableItem | null
  ) => void;
  selectedOptionId?: ExperienceOptionId;
  setSelectedOptionId: (optionId: ExperienceOptionId | undefined) => void;
  selectedTime: string | null;
  setSelectedTime: (selectedTime: string) => void;
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  parsedAgeBands?: { [key in ExperiencesAgeBandEnum]?: ParsedAgeBand };
  setTravelerCounts: (counts: IExperiencesPassengerCounts) => void;
  travelerCounts?: IExperiencesPassengerCounts;
  pricingBreakdowns?: ExperiencesPriceBreakdown[];
  largestValueAccount?: RewardsAccount;
  checkoutBreakdownProps?: IShopCheckoutBreakdown | null;
  disableCheckout?: boolean;
  isDatelessSearch?: boolean;
  cancellationPolicyProps?: IShopCancellationPolicyProps | null;
  noAvailability?: boolean;
}

const Skeleton = () => (
  <Box className={styles["experiences-shop-skeleton"]}>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="medium" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="large" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="large" />
    </Box>
    <Box className={styles["section-wrapper"]}>
      <ShopComponentSkeleton name="medium" />
      <ShopComponentSkeleton name="small" />
      <ShopComponentSkeleton name="medium" />
    </Box>
  </Box>
);

export const ShopMobileAvailabilityCard = (
  props: IShopMobileAvailabilityCardProps
) => {
  const {
    isSkeleton = false,
    experienceId,
    bookableItems,
    fromDate,
    untilDate,
    setFromDate,
    setUntilDate,
    fetchExperiencesShop,
    fetchExperiencesShopOptions,
    selectedBookableItem,
    setSelectedBookableItem,
    selectedTime,
    setSelectedTime,
    parsedAgeBands,
    setTravelerCounts,
    travelerCounts,
    selectedOptionId,
    setSelectedOptionId,
    selectedDate,
    setSelectedDate,
    experienceMaxTravelers,
    largestValueAccount,
    pricingCategory,
    checkoutBreakdownProps,
    adultRequired,
    timeSelectOptions,
    disableCheckout,
    pricingOpaque,
    isDatelessSearch = false,
    cancellationPolicyProps,
    noAvailability,
  } = props;

  const history = useHistory();
  const [openTravelerCountPicker, setOpenTravelerCountPicker] = useState(false);
  const [openBookableOptionPicker, setOpenBookableOptionPicker] =
    useState(false);

  const [localFromDate, setLocalFromDate] = useState<Date | null>(null);
  const [localUntilDate, setLocalUntilDate] = useState<Date | null>(null);

  const [modalTitles, setModalTitles] = useState<
    PassengerCountPickerTitles | undefined
  >(undefined);
  const [totalTravelers, setTotalTravelers] = useState<number>(2);
  const [openCalendarModal, setOpenCalendarModal] = React.useState(false);

  const [variantMax, setVariantMax] = useState<number | undefined>(
    experienceMaxTravelers
  );

  const handleFetchShopOptions = (
    travelersCount: IExperiencesPassengerCounts
  ) => {
    if (selectedBookableItem && selectedDate && experienceId) {
      const timeSlotPrices =
        selectedBookableItem.timeSlotsByDate[selectedDate] || [];

      const ageBandPrices =
        selectedBookableItem.ageBandPricesByDate[selectedDate] || [];

      fetchExperiencesShopOptions({
        experienceId,
        experienceOptionId: selectedBookableItem.experienceOptionId ?? selectedOptionId,
        paxMix: getPaxMixFromTravelersCount(travelersCount),
        date: selectedDate,
        currency: "USD",
        shopPricingOpaque: pricingOpaque,
        timeSlotPrices,
        ageBandPrices,
      });
    }
  };

  useEffect(() => {
    setTotalTravelers(getTotalTravelersCount(travelerCounts));
  }, [travelerCounts]);

  const isMultiVariant = useMemo(
    () => bookableItems && bookableItems.length > 1,
    [bookableItems]
  );

  useEffect(() => {
    if (parsedAgeBands) {
      if (parsedAgeBands.Adult) {
        const newModalTitles: AgeBandCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          adultTitle: textConstants.ADULTS,
          adultSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Adult?.minAge,
            parsedAgeBands.Adult?.maxAge
          ),
          adultHelperText: parsedAgeBands.Adult
            ? getMinMaxHelperText(
                parsedAgeBands.Adult.minNumberOfTravelers,
                parsedAgeBands.Adult.maxNumberOfTravelers
              )
            : undefined,
          adultPrice: parsedAgeBands.Adult
            ? textConstants.makePriceString(parsedAgeBands.Adult)
            : undefined,
          childrenPrice: parsedAgeBands.Child
            ? textConstants.makePriceString(parsedAgeBands.Child)
            : undefined,
          childrenTitle: parsedAgeBands.Child ? textConstants.CHILDREN : "",
          childrenSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Child?.minAge,
            parsedAgeBands.Child?.maxAge
          ),
          childrenHelperText: parsedAgeBands.Child
            ? getMinMaxHelperText(
                parsedAgeBands.Child.minNumberOfTravelers,
                parsedAgeBands.Child.maxNumberOfTravelers
              )
            : undefined,
          seniorTitle: parsedAgeBands.Senior
            ? textConstants.SENIORS
            : undefined,
          seniorSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Senior?.minAge,
            parsedAgeBands.Senior?.maxAge
          ),
          seniorHelperText: parsedAgeBands.Senior
            ? getMinMaxHelperText(
                parsedAgeBands.Senior.minNumberOfTravelers,
                parsedAgeBands.Senior.maxNumberOfTravelers
              )
            : undefined,
          seniorPrice: parsedAgeBands.Senior
            ? textConstants.makePriceString(parsedAgeBands.Senior)
            : undefined,
          youthTitle: parsedAgeBands.Youth ? textConstants.YOUTHS : undefined,
          youthSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Youth?.minAge,
            parsedAgeBands.Youth?.maxAge
          ),
          youthHelperText: parsedAgeBands.Youth
            ? getMinMaxHelperText(
                parsedAgeBands.Youth.minNumberOfTravelers,
                parsedAgeBands.Youth.maxNumberOfTravelers
              )
            : undefined,
          youthPrice: parsedAgeBands.Youth
            ? textConstants.makePriceString(parsedAgeBands.Youth)
            : undefined,
          infantTitle: parsedAgeBands.Infant
            ? textConstants.INFANTS
            : undefined,
          infantSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Infant?.minAge,
            parsedAgeBands.Infant?.maxAge
          ),
          infantHelperText: parsedAgeBands.Infant
            ? getMinMaxHelperText(
                parsedAgeBands.Infant.minNumberOfTravelers,
                parsedAgeBands.Infant.maxNumberOfTravelers
              )
            : undefined,
          infantPrice: parsedAgeBands.Infant
            ? textConstants.makePriceString(parsedAgeBands.Infant)
            : undefined,
        };
        setModalTitles(newModalTitles);
      } else if (parsedAgeBands.Traveler) {
        const newModalTitles: TravelerCountPickerTitles = {
          modalTitle: textConstants.EDIT_TRAVELERS,
          travelerTitle: textConstants.TRAVELER,
          travelerSubtitle: textConstants.AGES_SUBTITLE(
            parsedAgeBands.Traveler?.minAge,
            parsedAgeBands.Traveler?.maxAge
          ),
          travelerHelperText: parsedAgeBands.Traveler
            ? getMinMaxHelperText(
                parsedAgeBands.Traveler.minNumberOfTravelers,
                parsedAgeBands.Traveler.maxNumberOfTravelers
              )
            : undefined,
          travelerPrice: parsedAgeBands.Traveler
            ? textConstants.makePriceString(parsedAgeBands.Traveler)
            : undefined,
        };
        setModalTitles(newModalTitles);
      }

      let newVariantMax = 0;

      Object.values(parsedAgeBands).forEach((value) => {
        if (value.maxNumberOfTravelers !== undefined) {
          newVariantMax += value.maxNumberOfTravelers;
        } else {
          newVariantMax = experienceMaxTravelers ?? Infinity;
        }
      });

      setVariantMax(newVariantMax);
    }
  }, [parsedAgeBands, selectedBookableItem, selectedDate, travelerCounts, experienceMaxTravelers]);

  useEffect(() => {
    if (bookableItems) {
      setSelectedBookableItem(bookableItems[0]);
    }
  }, [bookableItems]);

  useEffect(() => {
    if (selectedBookableItem) {
      if (selectedBookableItem.experienceOptionId) {
        setSelectedOptionId(selectedBookableItem.experienceOptionId);
      }
      const dateToSelect = Object.keys(selectedBookableItem.timeSlotsByDate)[0];
      setSelectedDate(dateToSelect);
    }
  }, [selectedBookableItem]);

  useEffect(() => {
    if (timeSelectOptions) {
      setSelectedTime(timeSelectOptions[0]);
    }
  }, [timeSelectOptions]);

  const prevFetchKeyRef = useRef<string>("");
  useEffect(() => {
    if (selectedBookableItem && selectedDate && travelerCounts) {
      const totalTravelerCount = Object.values(travelerCounts).reduce((a: number, b: number) => a + b, 0);
      const fetchKey = `${selectedBookableItem.experienceOptionId?.value}-${selectedDate}-${totalTravelerCount}`;
      if (fetchKey !== prevFetchKeyRef.current) {
        prevFetchKeyRef.current = fetchKey;
        handleFetchShopOptions(travelerCounts);
      }
    }
  }, [selectedBookableItem, selectedDate, travelerCounts, handleFetchShopOptions]);

  useEffect(() => {
    setLocalFromDate(fromDate);
  }, [fromDate]);

  useEffect(() => {
    setLocalUntilDate(untilDate);
  }, [untilDate]);

  const getPriceTitle = (
    item: ParsedExperienceBookableItem
  ): {
    priceTitle: string;
    rewardsTitle: string;
    subtitle: string | undefined;
  } => {
    const { firstAvailablePrice } = item;

    const priceTitle = firstAvailablePrice?.price
      ? textConstants.PRICE_TITLE(firstAvailablePrice.price, pricingCategory)
      : "";

    const rewardsTitle =
      firstAvailablePrice?.price && largestValueAccount
        ? `${getRewardText({
            reward:
              firstAvailablePrice?.price.rewards[
                largestValueAccount.accountReferenceId
              ],
          })}`
        : "";

    let subtitle = experienceMaxTravelers
      ? textConstants.MAX_TRAVELER_TEXT(experienceMaxTravelers)
      : undefined;

    if (
      parsedAgeBands?.Adult?.priceVariesByCount ||
      parsedAgeBands?.Traveler?.priceVariesByCount
    ) {
      subtitle = textConstants.PRICE_VARIES_ON_GROUP_SIZE;
    }

    return {
      priceTitle,
      rewardsTitle,
      subtitle,
    };
  };

  const unavailableDates = useMemo(
    () =>
      selectedBookableItem?.dailyPrices
        ?.filter((daily) => daily.price === undefined)
        .map((daily) => dayjs(daily.date).toDate()),
    [selectedBookableItem]
  );

  const pricePerDay: { [date: string]: string } = useMemo(() => {
    const priceDateMap: { [date: string]: string } = {};
    selectedBookableItem?.dailyPrices
      ?.filter((daily) => daily.price)
      .forEach((daily) => {
        const date = dayjs(daily.date).format("YYYY-MM-DD");
        const { price } = daily;
        if (price) {
          priceDateMap[date] = getPriceString({
            price: price.fiat.value,
            currencySymbol: price.fiat.currencySymbol,
          });
        }
      });

    return priceDateMap;
  }, [selectedBookableItem]);

  const renderBookableItem = (item: ExperienceBookableItem) => (
    <Box
      className={clsx(styles["experiences-shop-mobile-availability-card"], {
        [styles["multi-variant"]]: isMultiVariant,
      })}
    >
      {/* Dateless: hide the date carousel, show traveler/time/date inputs
          inline instead. The else branch below is the original pre-dateless
          rendering, unchanged except for re-indentation. */}
      {isDatelessSearch ? (
        <>
          {isMultiVariant && checkoutBreakdownProps && (
            <ShopCheckoutBreakdownSummary
              {...checkoutBreakdownProps}
              parsedAgeBands={parsedAgeBands}
              largestValueAccount={largestValueAccount}
              cancellationPolicyProps={null}
            />
          )}
          <Box className={clsx(styles["experiences-shop-overview-bookables"])}>
            <Typography variant="h6">
              {selectedTime
                ? textConstants.CONFIRM_TRAVELERS_WITH_START_TIME
                : textConstants.CONFIRM_TRAVELERS}
            </Typography>
            <Box
              className={styles["experiences-shop-overview-bookable-selections"]}
            >
              <MobileOutlinedTextInput
                className="num-traveler-count"
                prefixString={TOTAL_NUM_TRAVELERS(totalTravelers)}
                icon={faUser}
                customEndIcon={<Icon name={IconName.B2BEditPencil} />}
                showCloseIcon={false}
                onClick={() => setOpenTravelerCountPicker(true)}
                value=""
              />
              {selectedTime && (
                <MobileOutlinedTextInput
                  label={START_TIME}
                  className="time-input-container"
                  prefixString={selectedTime}
                  customIcon={
                    <Icon className={styles["clock-icon"]} name="clock-icon" />
                  }
                  customEndIcon={<Icon name={IconName.B2BEditPencil} />}
                  showCloseIcon={false}
                  onClick={() => setOpenBookableOptionPicker(true)}
                  value=""
                />
              )}
              {/* New for dateless: date picker input replaces the date carousel */}
              <MobileOutlinedTextInput
                label={DATE}
                className="date-input-container"
                prefixString={
                  selectedDate
                    ? dayjs(selectedDate).format("ddd, MMM D")
                    : textConstants.SELECT_DATE
                }
                customIcon={
                  <Icon name={IconName.CalendarWithBoxIcon} />
                }
                customEndIcon={<Icon name={IconName.B2BEditPencil} />}
                showCloseIcon={false}
                onClick={() => setOpenCalendarModal(true)}
                value=""
              />
            </Box>
          </Box>
          {isMultiVariant && checkoutBreakdownProps && (
            <ShopCheckoutBreakdown
              {...checkoutBreakdownProps}
              largestValueAccount={largestValueAccount}
              cancellationPolicyProps={cancellationPolicyProps ?? null}
              mobile
              showSummary={false}
              noAvailability={noAvailability}
            />
          )}
        </>
      ) : (
        /* Original (dated) rendering — unchanged, just re-indented inside
           the ternary. Date carousel + travelers + time inputs. */
        <>
          <Box className={styles["experiences-shop-mobile-availability-content"]}>
            <Box className={styles["experiences-shop-mobile-availability-header"]}>
              <Typography variant="h6">{textConstants.AVAILABILITY}</Typography>
              <ActionLink
                content={
                  <Box className={styles["view-all-calendar-link-content"]}>
                    <Icon name={IconName.CalendarWithBoxIcon} />
                    <Typography className={styles["view-all-calendar-link-text"]}>
                      {textConstants.VIEW_ALL}
                    </Typography>
                  </Box>
                }
                onClick={() => setOpenCalendarModal(true)}
                className={styles["view-all-calendar-link"]}
              />
            </Box>
            {item.datesPastSearchedRange && (
              <NotificationBanner
                className={styles["unavailable-dates-banner"]}
                label={textConstants.AVAILABILITY_EXCEEDS_DATE_RANGE}
                severity={BannerSeverity.INFO}
                icon={
                  <Icon
                    className="banner-icon"
                    name={IconName.CalendarWithBoxIcon}
                  />
                }
              />
            )}
            <Box
              className={styles["experiences-shop-mobile-availability-selections"]}
            >
              {bookableItems &&
                Object.keys(item.timeSlotsByDate)
                  .slice(0, isMultiVariant ? 4 : 5)
                  .map((date) => (
                    <ActionButton
                      className={clsx(
                        styles[
                          "experiences-shop-mobile-availability-bookable-options-button"
                        ],
                        {
                          [styles["selected-button"]]: date === selectedDate,
                        }
                      )}
                      defaultStyle={
                        date === selectedDate ? "h4r-primary" : "h4r-secondary"
                      }
                      onClick={() => {
                        if (date !== selectedDate) {
                          setSelectedDate(date);
                        }
                      }}
                      message={
                        <Box
                          className={
                            styles[
                              "experiences-shop-mobile-availability-bookable-options-button-label"
                            ]
                          }
                        >
                          <span
                            className={
                              styles[
                                "experiences-shop-mobile-availability-bookable-options-button-day"
                              ]
                            }
                          >
                            {dayjs(date).format("ddd")}
                          </span>
                          <span
                            className={
                              styles[
                                "experiences-shop-mobile-availability-bookable-options-button-date"
                              ]
                            }
                          >
                            {dayjs(date).format("D")}
                          </span>
                          <span
                            className={
                              styles[
                                "experiences-shop-mobile-availability-bookable-options-button-month"
                              ]
                            }
                          >
                            {dayjs(date).format("MMM")}
                          </span>
                        </Box>
                      }
                    />
                  ))}
            </Box>
          </Box>
          {isMultiVariant && checkoutBreakdownProps && (
            <ShopCheckoutBreakdownSummary
              {...checkoutBreakdownProps}
              parsedAgeBands={parsedAgeBands}
              largestValueAccount={largestValueAccount}
              cancellationPolicyProps={null}
            />
          )}
          <Box className={clsx(styles["experiences-shop-overview-bookables"])}>
            {selectedTime ? (
              <Typography variant="h6">
                {textConstants.CONFIRM_TRAVELERS_WITH_START_TIME}
              </Typography>
            ) : (
              <Typography variant="h6">
                {textConstants.CONFIRM_TRAVELERS}
              </Typography>
            )}
            <Box
              className={styles["experiences-shop-overview-bookable-selections"]}
            >
              <MobileOutlinedTextInput
                className="num-traveler-count"
                prefixString={TOTAL_NUM_TRAVELERS(totalTravelers)}
                icon={faUser}
                customEndIcon={<Icon name={IconName.B2BEditPencil} />}
                showCloseIcon={false}
                onClick={() => setOpenTravelerCountPicker(true)}
                value=""
              />
              {selectedTime && (
                <MobileOutlinedTextInput
                  label={START_TIME}
                  className="time-input-container"
                  prefixString={selectedTime}
                  customIcon={
                    <Icon className={styles["clock-icon"]} name="clock-icon" />
                  }
                  customEndIcon={<Icon name={IconName.B2BEditPencil} />}
                  showCloseIcon={false}
                  onClick={() => setOpenBookableOptionPicker(true)}
                  value=""
                />
              )}
            </Box>
          </Box>
          {isMultiVariant && checkoutBreakdownProps && (
            <ShopCheckoutBreakdown
              {...checkoutBreakdownProps}
              largestValueAccount={largestValueAccount}
              cancellationPolicyProps={null}
              mobile
              showSummary={false}
            />
          )}
        </>
      )}
    </Box>
  );

  const renderExpandedVariantLabel = (item: ExperienceBookableItem) => (
    <Box
      className={clsx(
        styles["experiences-shop-bookable-item-option-container"],
        styles["experiences-shop-expanded-bookable-item-option-container"]
      )}
    >
      <Typography
        id={item.experienceOptionId.value}
        variant="h6"
        className={styles["experiences-shop-bookable-item-option-title"]}
      >
        {item.title}
      </Typography>
      {renderBookableItem(item)}
    </Box>
  );

  const renderCollapsedVariantLabel = (item: ParsedExperienceBookableItem) => {
    const { priceTitle, rewardsTitle, subtitle } = getPriceTitle(item);
    return (
      <Box
        className={clsx(
          styles["experiences-shop-bookable-item-option-container"],
          styles["experiences-shop-collapsed-bookable-item-option-container"]
        )}
      >
        <Typography
          id={item.experienceOptionId.value}
          variant="h6"
          className={styles["experiences-shop-bookable-item-option-title"]}
        >
          {item.title}
        </Typography>
        {priceTitle && (
          <Box
            className={clsx(
              styles["experiences-shop-bookable-item-option-price-container"]
            )}
          >
            <SanitizedHtml
              as={Typography}
              className={styles["experiences-shop-bookable-item-option-price"]}
              html={textConstants.getPriceAndRewardsCopy({
                  price: priceTitle,
                  rewards: rewardsTitle,
                })}
            />
            {subtitle && (
              <Typography
                className={
                  styles["experiences-shop-bookable-item-option-subtitle"]
                }
                variant="body2"
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderMultiVariantBookableItems = () => (
    <Box>
      <Box className={styles["experiences-shop-bookable-item-selection"]}>
        {(bookableItems || []).map((item) => (
          <FormControlLabel
            className={styles["experiences-shop-bookable-item-option"]}
            key={item.experienceOptionId.value}
            value={item.experienceOptionId.value}
            label={
              item.experienceOptionId.value === selectedOptionId?.value
                ? renderExpandedVariantLabel(item)
                : renderCollapsedVariantLabel(item)
            }
            labelPlacement="end"
            control={
              <Radio
                checked={
                  selectedOptionId?.value === item.experienceOptionId?.value
                }
                className={clsx(
                  styles["experiences-shop-bookable-item-option-radio"],
                  {
                    [styles[
                      "experiences-shop-bookable-item-option-radio-checked"
                    ]]:
                      selectedOptionId?.value ===
                      item.experienceOptionId?.value,
                  }
                )}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    setSelectedBookableItem(item);
                  }
                }}
              />
            }
          />
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Box className={styles["experiences-shop-mobile-availability-section"]}>
        {bookableItems && (
          <Box className={styles["experiences-shop-bookable-items-section"]}>
            {isMultiVariant
              ? renderMultiVariantBookableItems()
              : renderBookableItem(bookableItems[0])}
          </Box>
        )}
        {travelerCounts &&
          setTravelerCounts &&
          parsedAgeBands &&
          modalTitles && (
            <ExperiencesMobilePopoverCard
              headerText={textConstants.EDIT_TRAVELERS}
              content={
                <PassengerCountPicker
                  minimumAdultsCount={0}
                  titles={modalTitles}
                  setPassengerCounts={(counts) => {
                    const travellers = counts as IExperiencesPassengerCounts;
                    setTravelerCounts(travellers);
                    handleFetchShopOptions(travellers);
                  }}
                  onClickApply={() => {
                    setOpenTravelerCountPicker(false);
                  }}
                  counts={travelerCounts}
                  className="b2b"
                  maximumTravelersCount={
                    min([experienceMaxTravelers, variantMax]) ?? 0
                  }
                  paxCountLimits={
                    parsedAgeBands.Adult
                      ? {
                          seniorsCount: {
                            min: parsedAgeBands?.Senior?.minNumberOfTravelers,
                            max: parsedAgeBands?.Senior?.maxNumberOfTravelers,
                          },
                          adultsCount: {
                            min:
                              parsedAgeBands?.Adult?.minNumberOfTravelers ||
                              parsedAgeBands?.Traveler?.minNumberOfTravelers,
                            max:
                              parsedAgeBands?.Adult?.maxNumberOfTravelers ||
                              parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                          },
                          youthsCount: {
                            min: parsedAgeBands?.Youth?.minNumberOfTravelers,
                            max: parsedAgeBands?.Youth?.maxNumberOfTravelers,
                          },
                          childrenCount: {
                            min: parsedAgeBands?.Child?.minNumberOfTravelers,
                            max: parsedAgeBands?.Child?.maxNumberOfTravelers,
                          },
                          infantsCount: {
                            min: parsedAgeBands?.Infant?.minNumberOfTravelers,
                            max: parsedAgeBands?.Infant?.maxNumberOfTravelers,
                          },
                        }
                      : {
                          travelersCount: {
                            min: parsedAgeBands?.Traveler?.minNumberOfTravelers,
                            max: parsedAgeBands?.Traveler?.maxNumberOfTravelers,
                          },
                        }
                  }
                  getPaxCountsError={(counts) => {
                    const passengerCounts = counts as IExperiencesAgeBandCounts;
                    let errorText = "";
                    if (adultRequired) {
                      errorText =
                        passengerCounts.adultsCount +
                          (passengerCounts.seniorsCount ?? 0) <=
                        0
                          ? textConstants.ADULT_REQUIRED
                          : "";
                    }

                    return errorText;
                  }}
                />
              }
              isOpen={openTravelerCountPicker}
              setOpen={setOpenTravelerCountPicker}
            />
          )}
        {selectedTime && timeSelectOptions && (
          <ExperiencesMobilePopoverCard
            headerText={textConstants.CHOOSE_START_TIME}
            content={
              <RadioGroup
                className={
                  styles[
                    "experiences-shop-mobile-availability-start-time-modal"
                  ]
                }
                value={selectedTime}
                onChange={(event) => {
                  const optionId = selectedBookableItem?.experienceOptionId;
                  setSelectedTime(event.target.value);
                  if (optionId) {
                    setSelectedOptionId(optionId);
                  }
                }}
              >
                {timeSelectOptions.map((timeOption) => (
                  <FormControlLabel
                    className={styles["start-time-option"]}
                    key={timeOption}
                    value={timeOption}
                    label={
                      <span className={styles["start-time-option-label"]}>
                        {timeOption}
                      </span>
                    }
                    labelPlacement="start"
                    control={
                      <Radio className={styles["start-time-option-radio"]} />
                    }
                  />
                ))}
              </RadioGroup>
            }
            isOpen={openBookableOptionPicker}
            setOpen={setOpenBookableOptionPicker}
          />
        )}
        <ExperiencesMobilePopoverCard
          headerText={
            <Box className={styles["experiences-shop-mobile-calendar-header"]}>
              <div>{textConstants.SELECT_DATE}</div>
              <div
                className={
                  styles["experiences-shop-mobile-calendar-header-subtitle"]
                }
              >
                {pricingCategory?.ExperiencePricingCategory ===
                ExperiencePricingCategoryEnum.PerGroup
                  ? textConstants.PRICE_PER_GROUP
                  : textConstants.PRICE_PER_ADULT}
              </div>
            </Box>
          }
          content={
            <MobileCalendarPicker
              fromDate={localFromDate}
              untilDate={localUntilDate}
              setFromDate={(val: Date | null) => {
                setLocalFromDate(val);
                setLocalUntilDate(val);
              }}
              setUntilDate={() => {}}
              handleSearch={() => {
                setOpenCalendarModal(false);
                let refetchExperiencesShop = false;
                if (
                  localFromDate?.getTime() !== fromDate?.getTime() ||
                  localUntilDate?.getTime() !== untilDate?.getTime()
                ) {
                  refetchExperiencesShop = true;
                }
                setFromDate(localFromDate);
                setUntilDate(localUntilDate);
                if (refetchExperiencesShop) {
                  setSelectedBookableItem(null);
                  setSelectedDate(null);
                  setSelectedOptionId(undefined);
                  fetchExperiencesShop();
                  window.scrollTo(0, 0);
                }
              }}
              pickerType={PickerType.DAY}
              unavailableDates={unavailableDates}
              pricePerDay={pricePerDay}
              buttonLabel={CONTINUE_TEXT}
            />
          }
          isOpen={openCalendarModal}
          setOpen={setOpenCalendarModal}
          className={styles["experiences-shop-mobile-calendar-popover"]}
        />
        {isSkeleton ? <Skeleton /> : null}
      </Box>
      <Box className={styles["experiences-shop-mobile-continue-to-checkout"]}>
        <Box className={styles["button-section"]}>
          <ActionButton
            buttonClassName={
              styles["experiences-shop-mobile-continue-to-checkout-button"]
            }
            disabled={!selectedBookableItem || disableCheckout}
            onClick={() =>
              history.push(`${PATH_BOOK}${history.location.search}`)
            }
            message={textConstants.CONTINUE_TO_CHECKOUT}
          />
        </Box>
      </Box>
    </>
  );
};
